import React, { ReactNode, ReactElement, useReducer, useContext } from 'react';
import { v4 as uuid } from 'uuid';
import { Toasts, ToastType } from '@defa/defa-component-library';

enum NotificationActionType {
    'ADD',
    'REMOVE',
}

export interface Notification {
    id?: string;
    title?: string;
    message: string;
    type?: ToastType;
}
interface NotificationContextState extends NotificationState {
    add: (notification: Notification) => void;
    remove: (notification: Notification) => void;
}
interface NotificationState {
    notifications: Notification[];
}
interface NotificationAction {
    type: NotificationActionType;
    notification: Notification;
}

const NotificationContext = React.createContext<NotificationContextState | null>(null);

const initialState: NotificationState = { notifications: [] };

export function reducer(state: NotificationState, action: NotificationAction) {
    switch (action.type) {
        case NotificationActionType.ADD:
            return {
                notifications: [...state.notifications, { id: uuid(), ...action.notification }],
            };
        case NotificationActionType.REMOVE:
            return {
                notifications: [
                    ...state.notifications.filter((item) => item.id !== action.notification?.id),
                ],
            };
        default:
            throw new Error();
    }
}

export function NotificationProvider({ children }: { children: ReactNode }): ReactElement {
    const [state, dispatch] = useReducer(reducer, initialState);

    const add = (notification: Notification) => {
        dispatch({
            type: NotificationActionType.ADD,
            notification,
        });
    };

    const remove = (notification: Notification) => {
        dispatch({
            type: NotificationActionType.REMOVE,
            notification,
        });
    };

    const contextState = { ...state, add, remove };
    return (
        <NotificationContext.Provider value={contextState}>
            {children}
            <Toasts notifications={state.notifications} remove={remove} />
        </NotificationContext.Provider>
    );
}

export function useNotification() {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within NotificationProvider');
    }
    return context;
}
